import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Controller, useFormContext } from "react-hook-form";
import useTranslation from "../../../../shared/hooks/useTranslation";
import {
  OfferFile,
  OfferFormData,
  WarrantyPeriodCode,
  OfferPaymentTermsCode,
  ValidityPeriodCode,
} from "../../models/models";
import DropdownIcon from "../../../../shared/assets/icons/DropdownIcon";
import ServiceReportUploadFiles from "../../../machinery/components/ServiceReportUploadFile";
import DefaultIndicator from "../shared/DefaultIndicator";
import { dt } from "../../../../shared/themes/redesignTokens";

interface StepServiceNotesProps {
  files: OfferFile[];
  onFilesChange: (files: OfferFile[]) => void;
  fieldsFromDefaults?: Set<string>;
  onFieldChange?: (fieldName: string) => void;
}

const includedServiceFields = [
  "includeDiagnostics",
  "includeLaborForRepair",
  "includePartsAndMaterials",
  "includeTestingAndCalibration",
  "includeWorkAreaCleanup",
  "includeDetailedServiceReport",
] as const;

const StepServiceNotes = ({ files, onFilesChange, fieldsFromDefaults, onFieldChange }: StepServiceNotesProps) => {
  const { translate } = useTranslation(["serviceOffers"]);
  const { control, register, watch, trigger } = useFormContext<OfferFormData>();
  const [servicesExpanded, setServicesExpanded] = useState(false);
  const [termsExpanded, setTermsExpanded] = useState(false);

  const warrantyPeriodCode = watch("warrantyPeriodCode");
  const paymentTermsCode = watch("paymentTermsCode");
  const validityPeriodCode = watch("validityPeriodCode");

  const defaults = fieldsFromDefaults || new Set<string>();
  const handleFieldChange = (fieldName: string) => {
    if (onFieldChange) onFieldChange(fieldName);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {/* Section title */}
      <Box>
        <Box sx={{ fontFamily: dt.font, fontSize: "20px", fontWeight: 700, color: dt.textPrimary, mb: 0.5 }}>
          {translate("serviceNotesTitle")}
        </Box>
        <Box sx={{ fontFamily: dt.font, fontSize: "13px", color: dt.textMuted }}>
          {translate("serviceNotesHelperText")}
        </Box>
      </Box>

      {/* Service Notes textarea */}
      <Box
        sx={{
          background: dt.cardBg,
          borderRadius: dt.radius,
          border: `1px solid ${dt.cardBorder}`,
          boxShadow: dt.cardShadow,
          p: 2.5,
        }}
      >
        <InputLabel
          sx={{
            fontFamily: dt.font,
            fontSize: "12px",
            fontWeight: 600,
            color: dt.textSecondary,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            mb: 1,
          }}
        >
          {translate("serviceNotesLabel")}
        </InputLabel>
        <Controller
          control={control}
          name="serviceApproach"
          render={({ field }) => (
            <TextField
              className="textarea"
              value={field.value || ""}
              onChange={field.onChange}
              onBlur={field.onBlur}
              fullWidth
              multiline
              rows={4}
              placeholder={translate("serviceApproachPlaceholder")}
              inputProps={{ maxLength: 2000 }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontFamily: dt.font,
                  fontSize: "14px",
                  borderRadius: dt.radiusSm,
                },
              }}
            />
          )}
        />
      </Box>

      {/* Included Services — behind accordion */}
      <Accordion
        expanded={servicesExpanded}
        onChange={(_, expanded) => setServicesExpanded(expanded)}
        sx={{
          background: dt.cardBg,
          borderRadius: `${dt.radius} !important`,
          border: `1px solid ${dt.cardBorder}`,
          boxShadow: dt.cardShadow,
          "&:before": { display: "none" },
          "&.Mui-expanded": { margin: 0 },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            px: 2.5,
            "& .MuiAccordionSummary-content": { my: 1.5 },
          }}
        >
          <Box sx={{ fontFamily: dt.font, fontSize: "14px", fontWeight: 600, color: dt.textPrimary }}>
            {translate("includedServices")}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {includedServiceFields.map((fieldName) => (
              <Controller
                key={fieldName}
                control={control}
                name={fieldName}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={field.value || false}
                        onChange={(e) => {
                          field.onChange(e.target.checked);
                          handleFieldChange(fieldName);
                        }}
                        sx={{
                          color: dt.textMuted,
                          "&.Mui-checked": { color: dt.blue },
                        }}
                      />
                    }
                    label={
                      <Box sx={{ fontFamily: dt.font, fontSize: "13px", color: dt.textPrimary }}>
                        {translate(fieldName)}
                      </Box>
                    }
                  />
                )}
              />
            ))}
          </Box>
          <DefaultIndicator
            fieldName="includedServices"
            fieldsFromDefaults={defaults}
            groupFields={[...includedServiceFields]}
            label={translate("includedServicesFromDefaults")}
          />
        </AccordionDetails>
      </Accordion>

      {/* Attachments */}
      <Box
        sx={{
          background: dt.cardBg,
          borderRadius: dt.radius,
          border: `1px solid ${dt.cardBorder}`,
          boxShadow: dt.cardShadow,
          p: 2.5,
        }}
      >
        <InputLabel
          sx={{
            fontFamily: dt.font,
            fontSize: "12px",
            fontWeight: 600,
            color: dt.textSecondary,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            mb: 1.5,
          }}
        >
          {translate("attachments")}
        </InputLabel>
        <ServiceReportUploadFiles
          files={files || []}
          onChange={(newFiles) => onFilesChange(newFiles || [])}
          isCreate={true}
        />
      </Box>

      {/* Terms & Conditions */}
      {defaults.has("warrantyPeriodCode") || defaults.has("paymentTermsCode") || defaults.has("validityPeriodCode") ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            background: dt.blueGlow,
            border: `1px solid ${dt.blue}20`,
            borderRadius: dt.radiusSm,
            px: 2,
            py: 1.5,
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 18, color: dt.blue, mt: 0.2 }} />
          <Box sx={{ fontFamily: dt.font, fontSize: "12px", color: dt.textSecondary }}>
            {translate("termsPrefilledFromDefaults")}
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            background: dt.blueGlow,
            border: `1px solid ${dt.blue}20`,
            borderRadius: dt.radiusSm,
            px: 2,
            py: 1.5,
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 18, color: dt.blue, mt: 0.2 }} />
          <Box sx={{ fontFamily: dt.font, fontSize: "12px", color: dt.textSecondary }}>
            {translate("termsAutoFillPlaceholder")}
          </Box>
        </Box>
      )}

      <Accordion
        expanded={termsExpanded}
        onChange={(_, exp) => setTermsExpanded(exp)}
        sx={{
          background: dt.cardBg,
          borderRadius: `${dt.radius} !important`,
          border: `1px solid ${dt.cardBorder}`,
          boxShadow: dt.cardShadow,
          "&:before": { display: "none" },
          "&.Mui-expanded": { margin: 0 },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            px: 2.5,
            "& .MuiAccordionSummary-content": { my: 1.5 },
          }}
        >
          <Box sx={{ fontFamily: dt.font, fontSize: "14px", fontWeight: 600, color: dt.textPrimary }}>
            {translate("termsAndConditions")}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Warranty Period */}
            <Box>
              <InputLabel sx={{ fontFamily: dt.font, fontSize: "12px", fontWeight: 500, color: dt.textSecondary, mb: 0.5 }}>
                {translate("warrantyPeriod")}
              </InputLabel>
              <FormControl size="small" sx={{ width: "100%", maxWidth: "300px" }}>
                <Controller
                  control={control}
                  name="warrantyPeriodCode"
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={field.value || ""}
                      IconComponent={DropdownIcon}
                      sx={{ fontFamily: dt.font, fontSize: "13px", borderRadius: dt.radiusSm }}
                      onChange={(e) => {
                        field.onChange(e);
                        handleFieldChange("warrantyPeriodCode");
                      }}
                      onClose={() => setTimeout(() => trigger("warrantyPeriodCode"))}
                    >
                      <MenuItem value={WarrantyPeriodCode.thirtyDays}>{translate("warrantyPeriodCodes.thirtyDays")}</MenuItem>
                      <MenuItem value={WarrantyPeriodCode.ninetyDays}>{translate("warrantyPeriodCodes.ninetyDays")}</MenuItem>
                      <MenuItem value={WarrantyPeriodCode.sixMonths}>{translate("warrantyPeriodCodes.sixMonths")}</MenuItem>
                      <MenuItem value={WarrantyPeriodCode.oneYear}>{translate("warrantyPeriodCodes.oneYear")}</MenuItem>
                      <MenuItem value={WarrantyPeriodCode.custom}>{translate("warrantyPeriodCodes.custom")}</MenuItem>
                    </Select>
                  )}
                />
              </FormControl>
              <DefaultIndicator fieldName="warrantyPeriodCode" fieldsFromDefaults={defaults} />
              {Number(warrantyPeriodCode) === WarrantyPeriodCode.custom && (
                <TextField
                  size="small"
                  placeholder={translate("customWarrantyPeriodPlaceholder")}
                  {...register("customWarrantyPeriod", {
                    onChange: () => handleFieldChange("customWarrantyPeriod"),
                  })}
                  sx={{ mt: 1, maxWidth: "300px", "& .MuiOutlinedInput-root": { fontFamily: dt.font, fontSize: "13px", borderRadius: dt.radiusSm } }}
                />
              )}
            </Box>

            {/* Payment Terms */}
            <Box>
              <InputLabel sx={{ fontFamily: dt.font, fontSize: "12px", fontWeight: 500, color: dt.textSecondary, mb: 0.5 }}>
                {translate("paymentTerms")}
              </InputLabel>
              <FormControl size="small" sx={{ width: "100%", maxWidth: "300px" }}>
                <Controller
                  control={control}
                  name="paymentTermsCode"
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={field.value || ""}
                      IconComponent={DropdownIcon}
                      sx={{ fontFamily: dt.font, fontSize: "13px", borderRadius: dt.radiusSm }}
                      onChange={(e) => {
                        field.onChange(e);
                        handleFieldChange("paymentTermsCode");
                      }}
                      onClose={() => setTimeout(() => trigger("paymentTermsCode"))}
                    >
                      <MenuItem value={OfferPaymentTermsCode.paymentOnCompletion}>{translate("paymentTermsCodes.paymentOnCompletion")}</MenuItem>
                      <MenuItem value={OfferPaymentTermsCode.net30Days}>{translate("paymentTermsCodes.net30Days")}</MenuItem>
                      <MenuItem value={OfferPaymentTermsCode.fiftyAdvanceFiftyCompletion}>{translate("paymentTermsCodes.fiftyAdvanceFiftyCompletion")}</MenuItem>
                      <MenuItem value={OfferPaymentTermsCode.net60Days}>{translate("paymentTermsCodes.net60Days")}</MenuItem>
                      <MenuItem value={OfferPaymentTermsCode.custom}>{translate("paymentTermsCodes.custom")}</MenuItem>
                    </Select>
                  )}
                />
              </FormControl>
              <DefaultIndicator fieldName="paymentTermsCode" fieldsFromDefaults={defaults} />
              {Number(paymentTermsCode) === OfferPaymentTermsCode.custom && (
                <TextField
                  size="small"
                  placeholder={translate("customPaymentTermsPlaceholder")}
                  {...register("customPaymentTerms", {
                    onChange: () => handleFieldChange("customPaymentTerms"),
                  })}
                  sx={{ mt: 1, maxWidth: "300px", "& .MuiOutlinedInput-root": { fontFamily: dt.font, fontSize: "13px", borderRadius: dt.radiusSm } }}
                />
              )}
            </Box>

            {/* Validity Period */}
            <Box>
              <InputLabel sx={{ fontFamily: dt.font, fontSize: "12px", fontWeight: 500, color: dt.textSecondary, mb: 0.5 }}>
                {translate("offerValidityPeriod")}
              </InputLabel>
              <FormControl size="small" sx={{ width: "100%", maxWidth: "300px" }}>
                <Controller
                  control={control}
                  name="validityPeriodCode"
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={field.value || ""}
                      IconComponent={DropdownIcon}
                      sx={{ fontFamily: dt.font, fontSize: "13px", borderRadius: dt.radiusSm }}
                      onChange={(e) => {
                        field.onChange(e);
                        handleFieldChange("validityPeriodCode");
                      }}
                      onClose={() => setTimeout(() => trigger("validityPeriodCode"))}
                    >
                      <MenuItem value={ValidityPeriodCode.threeDays}>{translate("validityPeriodCodes.threeDays")}</MenuItem>
                      <MenuItem value={ValidityPeriodCode.fiveDays}>{translate("validityPeriodCodes.fiveDays")}</MenuItem>
                      <MenuItem value={ValidityPeriodCode.sevenDays}>{translate("validityPeriodCodes.sevenDays")}</MenuItem>
                      <MenuItem value={ValidityPeriodCode.fourteenDays}>{translate("validityPeriodCodes.fourteenDays")}</MenuItem>
                      <MenuItem value={ValidityPeriodCode.thirtyDays}>{translate("validityPeriodCodes.thirtyDays")}</MenuItem>
                      <MenuItem value={ValidityPeriodCode.custom}>{translate("validityPeriodCodes.custom")}</MenuItem>
                    </Select>
                  )}
                />
              </FormControl>
              <DefaultIndicator fieldName="validityPeriodCode" fieldsFromDefaults={defaults} />
              {Number(validityPeriodCode) === ValidityPeriodCode.custom && (
                <TextField
                  size="small"
                  placeholder={translate("customValidityPeriodPlaceholder")}
                  {...register("customValidityPeriod", {
                    onChange: () => handleFieldChange("customValidityPeriod"),
                  })}
                  sx={{ mt: 1, maxWidth: "300px", "& .MuiOutlinedInput-root": { fontFamily: dt.font, fontSize: "13px", borderRadius: dt.radiusSm } }}
                />
              )}
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default StepServiceNotes;
